import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { askToConfirm } from "../components/confirm";
import { CopiesHere, CopyToStaging } from "../components/StagingCopies";
import type { ReleaseChange, ReleaseRow, Releases, ReleaseVersion } from "../api/types";
import { useMe } from "../lib/me";

/** Admin → Releases (backend core/releases.py): what production and staging run, and the buttons that put
 * changes live - so nobody has to work out which Render service to deploy. Every button asks first, on the
 * page itself (askToConfirm). */

const when = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const both = (s: ("app" | "worker")[]) =>
  s.length === 2 ? "the app and the worker" : s[0] === "worker" ? "the worker" : "the app";

const KIND: Record<ReleaseRow["kind"], string> = {
  release: "Sent staging's version",
  back: "Went back",
  app: "Deployed the app again",
  worker: "Deployed the worker again",
};

// A deploy takes a few minutes; one still not live after this long has most likely failed in Render.
const DEPLOY_MINUTES = 20;

const deploying = (r?: ReleaseRow) =>
  !!r && !r.error && !r.live && Date.now() - new Date(r.at).getTime() < DEPLOY_MINUTES * 60_000;

function Version({ v }: { v?: ReleaseVersion | null }) {
  if (!v || !v.commit) return <span className="muted">not known yet</span>;
  return (
    <>
      <code>{v.short}</code>
      {v.title && (
        <span>
          {" "}· ends with <a href={v.url} target="_blank" rel="noreferrer">#{v.pr}</a> {v.title}
        </span>
      )}
    </>
  );
}

function Changes({ list }: { list: ReleaseChange[] }) {
  return (
    <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
      {list.map((c) => (
        <li key={c.pr}>
          <a href={c.url} target="_blank" rel="noreferrer">#{c.pr}</a> {c.title}
        </li>
      ))}
    </ul>
  );
}

function SetUp({ s }: { s: NonNullable<Releases["set_up"]> }) {
  const step = (ok: boolean, text: ReactNode) => (
    <li style={{ marginBottom: 6 }}>
      <span style={{ color: ok ? "var(--ok)" : "var(--muted)" }}>{ok ? "✓ Done" : "To do"}</span> · {text}
    </li>
  );
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3>One-time set-up</h3>
      <div className="muted" style={{ marginBottom: 8 }}>
        Three settings on the app in Render (Render → mockingbird → Environment → Add, then Save). Each is a secret,
        so paste it there yourself; saving restarts the app with no outage.
      </div>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        {step(s.app, <>The app's deploy link: Render → <b>mockingbird</b> → Settings → Deploy Hook → copy. Add it as{" "}
          <code>PROOFBOX_DEPLOY_HOOK_APP</code>.</>)}
        {step(s.worker, <>The worker's deploy link: Render → <b>mockingbird-worker</b> → Settings → Deploy Hook → copy.
          Add it on <b>mockingbird</b> as <code>PROOFBOX_DEPLOY_HOOK_WORKER</code>.</>)}
        {step(s.github, <>A read-only GitHub key: GitHub → Settings → Developer settings → Fine-grained tokens → Generate,
          for the <b>mockingbird</b> repository only, with Contents: Read-only and Actions: Read-only. Add it as{" "}
          <code>PROOFBOX_GITHUB_TOKEN</code>. It lets this page name the changes, check staging is newer and wait
          for GitHub's checks.</>)}
      </ol>
    </div>
  );
}

export function AdminReleases() {
  const qc = useQueryClient();
  const { data: me, isLoading: meLoading } = useMe();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "releases"],
    queryFn: () => api<Releases>("/api/admin/releases"),
    enabled: !!me?.is_superadmin,
    refetchInterval: (q) =>
      deploying(q.state.data?.history?.[0]) ? 5000 : q.state.data?.plan?.waiting ? 15000 : 30000,
  });
  const press = useMutation({
    mutationFn: (action: string) =>
      api<Releases>("/api/admin/releases", { method: "POST", body: JSON.stringify({ action }) }),
    onSuccess: (d) => qc.setQueryData(["admin", "releases"], d),
  });

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (!me?.is_superadmin) return <Navigate to="/" replace />;

  const go = async (action: string, question: string, yes: string, danger = false) => {
    if (await askToConfirm(question, { yes, danger })) press.mutate(action);
  };

  const head = (
    <div className="page-head">
      <div>
        <h1>Releases</h1>
        <div className="sub">What's live, what's waiting, and the buttons that put changes live.</div>
      </div>
      <button className="btn ghost sm" type="button" disabled={isFetching} onClick={() => refetch()}>
        {isFetching ? "Checking…" : "Check again"}
      </button>
    </div>
  );

  if (isLoading || !data) {
    return <div className="page admin-page">{head}<div className="muted">Checking what runs where…</div></div>;
  }
  if (!data.here) {
    return (
      <div className="page admin-page">
        {head}
        <div className="card">
          This is <b>{data.environment}</b>. Releases are made from production: open{" "}
          <a href="https://mockingbirdaudio.com/admin/releases">mockingbirdaudio.com → Admin → Releases</a>.
        </div>
        {data.environment === "staging" && <CopiesHere />}
      </div>
    );
  }

  const plan = data.plan ?? {};
  const latest = data.history?.[0];
  const busy = deploying(latest) || press.isPending;
  const jobs = data.worker_jobs ?? [];
  const setUpDone = !!data.set_up && Object.values(data.set_up).every(Boolean);

  return (
    <div className="page admin-page">
      {head}

      {data.set_up && !setUpDone && <SetUp s={data.set_up} />}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3>Ready to go live</h3>
        {deploying(latest) ? (
          <div>
            <b>Deploying {both(latest!.services)}…</b> started {when(latest!.at)}. It usually takes 2 to 3 minutes;
            this page checks every few seconds and shows a tick below once it's live. Nobody is interrupted meanwhile.
          </div>
        ) : plan.commit ? (
          <>
            <div>
              Staging's version (<code>{plan.short}</code>) is ready to send to production: <b>{plan.why}</b>.
            </div>
            {!!plan.changes?.length && <Changes list={plan.changes} />}
            {plan.note && <div className="muted" style={{ marginTop: 10 }}>{plan.note}</div>}
            {plan.services?.includes("worker") && jobs.length > 0 && (
              <div className="muted" style={{ marginTop: 10 }}>
                Running on the worker now: {jobs.map((j) => `${j.label}${j.project ? ` (${j.project})` : ""}`).join(", ")}.
                Jobs that work through a list (transcribing, Noise Analytics) pause and carry on after the deploy;
                others get 4½ minutes to finish, or start again on the new version.
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <button className="btn primary" type="button" disabled={busy} onClick={() => go("release", `Send staging's version (${plan.short}) to production: ${plan.why}?`, "Yes, send it")}>
                {press.isPending ? "Starting…" : "Send staging's version to production"}
              </button>
            </div>
          </>
        ) : (
          <div style={{ color: plan.done ? "var(--ok)" : "var(--muted)" }}>
            {plan.done ? "✓ " : ""}{plan.blocked}
          </div>
        )}
        {!!data.coming?.length && (
          <div className="muted" style={{ marginTop: 12 }}>
            On its way to staging (merged; staging updates itself a few minutes after each merge):
            <Changes list={data.coming} />
          </div>
        )}
        {press.isError && <div className="sw-error">{(press.error as Error).message}</div>}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3>What runs where</h3>
        <table className="table">
          <tbody>
            <tr><td style={{ width: 170 }}>Production: the app</td><td><Version v={data.app} /></td>
              <td className="muted">{data.app?.since ? `since ${when(data.app.since)}` : ""}</td></tr>
            <tr><td>Production: the worker</td><td><Version v={data.worker} /></td>
              <td className="muted">{data.worker?.since ? `since ${when(data.worker.since)}` : ""}</td></tr>
            <tr><td>Staging</td><td>{data.staging ? <Version v={data.staging} /> : <span className="muted">not known just now</span>}</td>
              <td /></tr>
          </tbody>
        </table>
      </div>

      <CopyToStaging />

      <div className="card" style={{ marginBottom: 16 }}>
        <h3>Other buttons</h3>
        <div className="muted" style={{ marginBottom: 10 }}>For when something's wrong. Each asks before it does anything.</div>
        <div style={{ display: "grid", gap: 10 }}>
          {data.back && (
            <div>
              <button className="btn danger sm" type="button" disabled={busy} onClick={() => go("back", `Go back to the version from before ${when(data.back!.when)} (${data.back!.short})? Only if the latest release broke something.`, "Yes, go back", true)}>
                Go back to the previous version
              </button>{" "}
              <span className="muted">
                Undoes the latest release ({data.back.of}, {when(data.back.when)}): production returns to <code>{data.back.short}</code>.
              </span>
            </div>
          )}
          <div>
            <button className="btn ghost sm" type="button" disabled={busy || !data.set_up?.app} onClick={() => go("app", "Deploy the app again, at the version it runs? (No outage.)", "Yes, deploy it")}>
              Deploy the app again
            </button>{" "}
            <span className="muted">Same version, fresh start: if the site seems stuck.</span>
          </div>
          <div>
            <button className="btn ghost sm" type="button" disabled={busy || !data.set_up?.worker} onClick={() => go("worker", "Deploy the worker again, at the version it runs? Long jobs pause and carry on.", "Yes, deploy it")}>
              Deploy the worker again
            </button>{" "}
            <span className="muted">Same version, fresh start: if background work seems stuck.</span>
          </div>
        </div>
      </div>

      {!!data.history?.length && (
        <div className="card">
          <h3>Recent releases</h3>
          <table className="table">
            <tbody>
              {data.history.map((r, i) => (
                <tr key={`${r.at}-${i}`}>
                  <td className="muted" style={{ width: 130 }}>{when(r.at)}</td>
                  <td>{KIND[r.kind]}{r.short && <> <code>{r.short}</code></>}{r.services.length ? ` to ${both(r.services)}` : ""}</td>
                  <td className="muted">{r.by}</td>
                  <td style={{ color: r.error ? "var(--bad)" : r.live ? "var(--ok)" : "var(--muted)" }}>
                    {r.error ? `Didn't start: ${r.error}` : r.live ? "✓ Live" : deploying(r) ? "Deploying…" : "Replaced since"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
